import axios from "axios";
import { buildPayload, validateBooking, ERRORS, REQUEST_TIMEOUT_MS } from "./contract";
import { FASTAPI_BASE_URL } from "./config";

/**
 * =============================================================================
 * ADAPTER — FastAPI backend (the service in /backend)
 * =============================================================================
 *
 * Implements the contract in ./contract.js. This is a full implementation, not a
 * stub: it is the default adapter and it works the moment a backend is reachable.
 *
 * It wraps what Booking.jsx used to do inline — POST application/json to
 * `${base}/api/bookings` with an 18s timeout — and nothing about the request on
 * the wire has changed. The backend needs no modification to serve it.
 *
 * -----------------------------------------------------------------------------
 * WHY application/json IS CORRECT HERE (and text/plain is correct next door)
 * -----------------------------------------------------------------------------
 * The opposite of the Apps Script adapter, and for a good reason. FastAPI parses
 * a JSON body, and Starlette's CORSMiddleware answers the OPTIONS preflight
 * properly — so triggering a preflight costs one extra round trip and nothing
 * else. The two adapters differ here because the backends genuinely differ, not
 * because one of them is a mistake.
 *
 * For the preflight to succeed, the deployed origin must be in ALLOWED_ORIGINS
 * on the backend (fix pass 1). A missing origin shows up here as ERRORS.NETWORK
 * — a CORS block gives JavaScript no response object to inspect, so it is
 * indistinguishable from being offline. If this adapter reports `network` while
 * the backend is demonstrably up, check the allowlist first.
 *
 * -----------------------------------------------------------------------------
 * WHY THIS RESOLVES OPTIMISTICALLY TOO
 * -----------------------------------------------------------------------------
 * FastAPI *does* return a readable body — `POST /api/bookings` responds with the
 * created BookingRequest including its `id`, and this adapter passes that `id`
 * through when present.
 *
 * But success is still reported the same way as the Apps Script adapter: a
 * completed request means ok. We do not gate the success panel on having parsed
 * an id, because the Apps Script adapter can never provide one, and a UI that
 * behaved differently depending on the adapter would leak the abstraction
 * straight back into the component. See ./index.js.
 *
 * So `id` here is a bonus for logging, not a contract guarantee.
 */

/**
 * @param   {import("./contract").BookingSubmission} data
 * @returns {Promise<import("./contract").BookingResult>}
 */
export async function submitBooking(data) {
  if (!FASTAPI_BASE_URL) {
    // Previously this produced the literal URL "undefined/api/bookings", which
    // the browser resolved against the site origin and the host answered with a
    // 404 — a confusing failure that looked like a routing bug. Now it is named.
    return { ok: false, error: ERRORS.NOT_CONFIGURED };
  }

  const payload = buildPayload(data);

  const { valid, missing } = validateBooking(payload);
  if (!valid) {
    return { ok: false, error: `${ERRORS.INVALID}: ${missing.join(",")}` };
  }

  try {
    const { data: body } = await axios.post(`${FASTAPI_BASE_URL}/api/bookings`, payload, {
      headers: { "Content-Type": "application/json" },
      timeout: REQUEST_TIMEOUT_MS,
    });

    // Pass the id through when the backend supplied one; omit it otherwise.
    return body && body.id ? { ok: true, id: body.id } : { ok: true };
  } catch (err) {
    return { ok: false, error: classify(err) };
  }
}

/** Map an axios rejection onto a contract error code. */
function classify(err) {
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return ERRORS.TIMEOUT;
  }
  if (err?.response) {
    return `${ERRORS.SERVER}:${err.response.status}`;
  }
  // No response object: DNS failure, refused connection, offline, or a CORS
  // block — see the ALLOWED_ORIGINS note above.
  return ERRORS.NETWORK;
}
